// Navbar.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiSearch, FiGlobe, FiX, FiMenu, FiUser } from 'react-icons/fi';
import { AnimatePresence, motion } from 'framer-motion';
import rsbloglogo from '../assets/images/rsbloglogo.png';
import { useLanguage } from '../utils/LanguageContext';

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { language, toggleLanguage } = useLanguage();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activePage, setActivePage] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isScrolled, setIsScrolled] = useState(false);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) setIsMenuOpen(false);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const path = location.pathname;
    if (path.includes('blog')) setActivePage('blogs');
    else if (path.includes('infomatics')) setActivePage('infomatics');
    else if (path.includes('article')) setActivePage('articles');
    else if (path.includes('quotes')) setActivePage('quotes');
    else if (path.includes('speech')) setActivePage('speeches');
    else if (path === '/photos') setActivePage('photos');
    else if (path === '/videos') setActivePage('videos');
    else setActivePage('home');
  }, [location]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}&lang=${language}`);
      setSearchQuery('');
      setIsSearchFocused(false);
    }
  };

  const handleSearchFocus = () => {
    setIsSearchFocused(true);
  };

  const handleSearchBlur = () => {
    if (!searchQuery) {
      setIsSearchFocused(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setSearchQuery('');
      setIsSearchFocused(false);
      searchInputRef.current.blur();
    }
  };

  const handleAboutClick = () => {
    window.open('https://rajendrashende.com/', '_blank');
  };

  const navigateTo = (page) => {
    setIsMenuOpen(false);
    
    switch(page) {
      case 'blogs':
        navigate('/allblogs');
        break;
      case 'infomatics':
        navigate('/allinfomatics');
        break;
      case 'articles':
        navigate('/allarticles');
        break;
      case 'speeches':
        navigate('/allspeeches');
        break;
      case 'photos':
        navigate('/photos');
        break;
      case 'videos':
        navigate('/videos');
        break;
      default:
        navigate('/');
    }
  };

  const menuItems = [
    { id: 'blogs', label: { MR: 'अनुदिनी (ब्लॉग्ज)', EN: 'Blogs' } },
    { id: 'infomatics', label: { MR: 'निवेदन व संदेश', EN: 'Infomatics' } },
    { id: 'articles', label: { MR: 'लेख', EN: 'Articles' } },
    { id: 'speeches', label: { MR: 'भाषणे', EN: 'Speeches' } },
    { id: 'photos', label: { MR: 'फोटो गॅलरी', EN: 'Photos' } },
    { id: 'videos', label: { MR: 'व्हिडिओ गॅलरी', EN: 'Videos' } },
  ];

  return (
    <>
      <nav className={`py-2 px-4 md:px-6 flex items-center justify-between fixed w-full top-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-transparent' : 'bg-transparent'}`}>
        <div className="flex items-center">
          <img
            src={rsbloglogo}
            alt="RS Blog Logo"
            className="h-12 md:h-16 w-auto cursor-pointer"
            onClick={() => navigate('/')}
          />
        </div>

        <div className={`flex-grow flex justify-center mx-2 md:mx-8 ${isMobile && 'hidden'}`}>
          <motion.div 
            className="relative w-full max-w-xl"
            animate={{
              width: isSearchFocused ? '100%' : '100%',
            }}
            transition={{ type: 'spring', damping: 20 }}
          >
            <form onSubmit={handleSearch} className="relative">
              <motion.div 
                className={`flex items-center border-0 outline-none ring-0 focus-within:ring-0 focus-within:outline-none ${isSearchFocused ? 'border-b-2 border-red-600' : 'border-b border-red-600'}`}
                style={{ 
                  border: 'none', 
                  borderBottom: isSearchFocused ? '2px solid #dc2626' : '1px solid #dc2626',
                  outline: 'none',
                  boxShadow: 'none'
                }}
                animate={{
                  borderColor: '#dc2626',
                  borderWidth: isSearchFocused ? 2 : 1,
                }}
              >
                <FiSearch className="text-red-600 mr-2" size={20} />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder={language === 'MR' ? 'शोधा...' : 'Search...'}
                  maxLength={200}
                  className="w-full py-1 px-2 focus:outline-none bg-transparent border-0 ring-0 focus:ring-0 focus:border-0 shadow-none focus:shadow-none"
                  style={{ border: 'none', outline: 'none', boxShadow: 'none' }}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={handleSearchFocus}
                  onBlur={handleSearchBlur}
                  onKeyDown={handleKeyDown}
                />
                {searchQuery && (
                  <motion.button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="text-gray-500 hover:text-red-600 transition-colors"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <FiX size={20} />
                  </motion.button>
                )}
              </motion.div>
            </form>

            <AnimatePresence>
              {isSearchFocused && (
                <motion.div
                  className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-xl z-50 p-4"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ type: 'spring', damping: 25 }}
                >
                  <p className="text-gray-500 text-sm">
                    {language === 'MR' 
                      ? 'Enter दाबा किंवा शोध बटण दाबा' 
                      : 'Press Enter or click search button'}
                  </p>
                  <div className="mt-2 flex justify-end">
                    <button
                      onClick={handleSearch}
                      className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                    >
                      {language === 'MR' ? 'शोधा' : 'Search'}
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>

        <div className="flex items-center">
          <button
            onClick={handleAboutClick}
            className="flex items-center text-red-600 mr-2 md:mr-4 focus:outline-none hover:text-red-700"
          >
            <FiUser size={20} className="mr-1" />
            {!isMobile && <span>{language === 'MR' ? 'डॉ. आरएमएस बद्दल' : 'About Dr. RMS'}</span>}
          </button>
          
          <button
            onClick={toggleLanguage}
            className="flex items-center text-red-600 mr-2 md:mr-4 focus:outline-none hover:text-red-700"
          >
            <FiGlobe size={20} className="mr-1" />
            {!isMobile && <span>{language === 'MR' ? 'इंग्रजी' : 'मराठी'}</span>}
          </button>
          
          {isMobile && (
            <button
              onClick={() => setIsSearchFocused(!isSearchFocused)}
              className="text-red-600 p-2 mr-2"
            >
              <FiSearch size={24} />
            </button>
          )}
          
          <button
            onClick={toggleMenu}
            className={`relative px-3 py-1 md:px-4 md:py-2 text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors border-2 ${isMenuOpen ? 'border-white bg-red-700' : 'border-red-600'}`}
            style={{
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              zIndex: 60
            }}
          >
            {isMobile ? <FiMenu size={24} /> : (language === 'MR' ? 'मेनू' : 'Menu')}
          </button>
        </div>

        {/* Mobile Search Bar */}
        {isMobile && isSearchFocused && (
          <motion.div
            className="absolute top-0 left-0 right-0 bg-white p-4 z-50 shadow-md"
            initial={{ y: -100 }}
            animate={{ y: 0 }}
            exit={{ y: -100 }}
          >
            <form onSubmit={handleSearch} className="flex items-center">
              <button
                type="button"
                onClick={() => setIsSearchFocused(false)}
                className="text-gray-500 mr-2"
              >
                <FiX size={24} />
              </button>
              <div className="flex items-center flex-1 border-b border-red-600">
                <FiSearch className="text-red-600 mr-2" size={20} />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder={language === 'MR' ? 'शोधा...' : 'Search...'}
                  maxLength={200}
                  className="w-full py-2 focus:outline-none bg-transparent"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                />
              </div>
            </form>
          </motion.div>
        )}
      </nav>

      {/* Add padding to the top of the main content to prevent overlap */}
      <div className="pt-16 md:pt-20"></div>

      <AnimatePresence>
        {isMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-black bg-opacity-50"
              onClick={toggleMenu}
            />
            
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25 }}
              className="absolute right-0 h-full w-full md:w-80 bg-gradient-to-b from-white to-gray-50 text-gray-800 border-l border-gray-200"
              style={{ 
                zIndex: 50,
                top: 0,
                position: 'fixed',
                boxShadow: '-4px 0 12px rgba(0,0,0,0.1)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 h-full flex flex-col">
                <div className="flex justify-between items-center mb-8">
                  <h2 className="text-xl font-bold text-gray-800">{language === 'MR' ? 'मेनू' : 'Menu'}</h2>
                  <button onClick={toggleMenu} className="text-gray-600 hover:text-gray-800">
                    <FiX size={24} />
                  </button>
                </div>
                
                <div className="flex-1">
                  <ul className="space-y-2">
                    {menuItems.map((item) => (
                      <motion.li
                        key={item.id}
                        whileHover={{ x: 5 }}
                        className={`w-full py-3 px-4 rounded-lg transition-colors ${
                          activePage === item.id 
                            ? 'bg-red-600 text-white' 
                            : 'hover:bg-gray-100 text-gray-700'
                        }`}
                      >
                        <button
                          className="w-full text-left font-medium"
                          onClick={() => navigateTo(item.id)}
                        >
                          {item.label[language]}
                        </button>
                      </motion.li>
                    ))}
                  </ul>
                </div>
                
                <div className="pt-8 border-t border-gray-200">
                  <button
                    className="flex items-center justify-center w-full py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
                    onClick={toggleLanguage}
                  >
                    <FiGlobe size={20} className="mr-2" />
                    {language === 'MR' ? 'English मध्ये बदला' : 'Change to Marathi'}
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;